import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { incubatorsSchemas } from '../lib/schemas';

/* ── Hero ── */
const HeroSection = styled.section`
  background-color: #7AA6A2;
  padding: 6rem 2rem 7rem;
  text-align: center;
  position: relative;
  z-index: 0;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 65%;
    height: 100%;
    background-image: url('/img/background.png');
    background-repeat: no-repeat;
    background-size: cover;
    -webkit-mask-image: linear-gradient(to right, transparent 0%, black 50%);
    mask-image: linear-gradient(to right, transparent 0%, black 50%);
    pointer-events: none;
    z-index: -1;
  }
`;

const HeroTitle = styled.h1`
  color: #fff;
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const HeroButton = styled(Link)`
  display: inline-block;
  background-color: #7AA6A2;
  color: #fff;
  font-weight: 700;
  font-size: 1rem;
  padding: 0.85rem 2rem;
  border-radius: 100px;
  transition: background-color 0.2s, transform 0.15s;
  text-decoration: none;

  &:hover {
    background-color: #5a8a87;
    transform: translateY(-1px);
  }
`;

const HeroWave = styled.div`
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  line-height: 0;
  pointer-events: none;

  svg {
    width: 100%;
    height: 64px;
    display: block;
  }
`;

/* ── Lead (below hero) ── */
const LeadSection = styled.section`
  background-color: #fff;
  padding: 4rem 2rem 4.5rem;
  text-align: center;
`;

const LeadSubtitle = styled.h2`
  color: #333;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const LeadText = styled.p`
  color: #555;
  font-size: 1.2rem;
  line-height: 1.8;
  max-width: 620px;
  margin: 0 auto 2.5rem;
`;

const LeadButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

/* ── What is BAM ── */
const IntroSection = styled.section`
  padding: 5.5rem 2rem;
  max-width: 820px;
  margin: 0 auto;
  text-align: center;
`;

const SectionLabel = styled.p`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: var(--accent-color);
  margin-bottom: 0.75rem;
  font-weight: 600;
`;

const SectionTitle = styled.h2`
  color: #333;
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 1.25rem;
  line-height: 1.25;
  font-family: var(--font-heading);
`;

const BodyText = styled.p`
  color: #555;
  font-size: 1.05rem;
  line-height: 1.8;
  margin-bottom: 1.25rem;
  text-align: left;
`;

/* ── Journey steps ── */
const JourneySection = styled.section`
  background-color: #FAF9F8;
  padding: 5.5rem 2rem;
`;

const JourneyInner = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const JourneyHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const StepCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid #eee;
  box-shadow: 0 2px 10px rgba(0,0,0,0.04);
  position: relative;
`;

const StepNumber = styled.div`
  font-family: var(--font-heading);
  font-size: 3.5rem;
  font-weight: 700;
  color: var(--accent-color-light, #F5E9D0);
  line-height: 1;
  margin-bottom: 0.5rem;
`;

const StepTitle = styled.h3`
  color: #333;
  font-size: 1.15rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  font-family: var(--font-heading);
`;

const StepText = styled.p`
  color: #666;
  font-size: 0.98rem;
  line-height: 1.75;
  margin: 0;
`;

/* ── Who it's for ── */
const AudienceSection = styled.section`
  padding: 5.5rem 2rem;
  max-width: 900px;
  margin: 0 auto;
`;

const AudienceHeader = styled.div`
  text-align: center;
  margin-bottom: 3.5rem;
`;

const AudienceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const AudienceCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 1.75rem;
  border-left: 4px solid var(--primary-color);
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
`;

const AudienceIcon = styled.div`
  font-size: 1.75rem;
  margin-bottom: 0.75rem;
`;

const AudienceTitle = styled.h3`
  color: #333;
  font-size: 1.05rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  font-family: var(--font-heading);
`;

const AudienceText = styled.p`
  color: #666;
  font-size: 0.95rem;
  line-height: 1.7;
  margin: 0;
`;

/* ── Quote ── */
const QuoteSection = styled.section`
  background-color: #333;
  padding: 5rem 2rem;
  text-align: center;
`;

const QuoteMark = styled.div`
  font-family: var(--font-heading);
  font-size: 5rem;
  color: var(--accent-color);
  line-height: 0.5;
  margin-bottom: 1.5rem;
  opacity: 0.8;
`;

const QuoteText = styled.blockquote`
  font-family: var(--font-heading);
  font-style: italic;
  font-size: 1.6rem;
  color: #fff;
  max-width: 700px;
  margin: 0 auto 1.25rem;
  line-height: 1.5;
  font-weight: 400;

  @media (max-width: 600px) {
    font-size: 1.25rem;
  }
`;

const QuoteSource = styled.p`
  color: var(--accent-color);
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin: 0;
`;

/* ── CTA ── */
const CtaSection = styled.section`
  background-color: #7AA6A2;
  padding: 5.5rem 2rem;
  text-align: center;
`;

const CtaTitle = styled.h2`
  color: #fff;
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 1rem;
  font-family: var(--font-heading);
`;

const CtaText = styled.p`
  color: rgba(255,255,255,0.9);
  font-size: 1.1rem;
  max-width: 540px;
  margin: 0 auto 2.25rem;
  line-height: 1.75;
`;

const CtaButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const CtaButton = styled(Link)`
  display: inline-block;
  background-color: #fff;
  color: #7AA6A2;
  font-weight: 700;
  font-size: 1rem;
  padding: 0.9rem 2.25rem;
  border-radius: 100px;
  transition: background-color 0.2s, transform 0.15s;
  text-decoration: none;

  &:hover {
    background-color: #f0f8f7;
    color: #5a8a87;
    transform: translateY(-1px);
  }
`;

const CtaSecondary = styled(Link)`
  display: inline-block;
  background-color: transparent;
  color: #fff;
  font-weight: 600;
  font-size: 1rem;
  padding: 0.9rem 2.25rem;
  border-radius: 100px;
  border: 2px solid rgba(255,255,255,0.5);
  transition: border-color 0.2s, background-color 0.2s;
  text-decoration: none;

  &:hover {
    border-color: #fff;
    background-color: rgba(255,255,255,0.08);
    color: #fff;
  }
`;

/* ── Data ── */
const STEPS = [
  {
    number: '01',
    title: 'Discover Your Calling',
    text: 'We help you articulate why God has placed you in the marketplace and how your specific skills, sector, and location are part of His redemptive purpose — not incidental to it.',
  },
  {
    number: '02',
    title: 'Build on Biblical Foundations',
    text: 'Through teaching, coaching, and community, we ground your business vision in Scripture — helping you define success, leadership, and stewardship through a kingdom lens.',
  },
  {
    number: '03',
    title: 'Integrate Mission into Your Business',
    text: "We help you move beyond writing checks to a charity and toward embedding gospel witness into your company's culture, hiring, products, and community relationships.",
  },
  {
    number: '04',
    title: 'Grow with Accountability',
    text: 'You\'ll connect with a community of like-minded BAM practitioners who ask hard questions, share hard-won wisdom, and help you stay the course when business pressures push back.',
  },
];

const AUDIENCE = [
  {
    icon: '🏢',
    title: 'Established Business Owners',
    text: 'You\'ve built something successful and are asking how to make it count for more than profit. We help you reimagine your business as a platform for gospel impact.',
  },
  {
    icon: '🌱',
    title: 'Entrepreneurs Just Starting Out',
    text: 'You want to build a business from the ground up with mission baked in — not bolted on later. We give you the framework to do that from day one.',
  },
  {
    icon: '🌍',
    title: 'Marketplace Professionals',
    text: 'You don\'t own the company but you have influence where you work. We help you understand your role as a kingdom ambassador in your industry and organization.',
  },
  {
    icon: '⛪',
    title: 'Church & Ministry Leaders',
    text: 'You want to equip the business people in your congregation to see their work as ministry. We partner with churches to bring BAM teaching and coaching to your community.',
  },
];

const MinistryIncubators = () => {
  return (
    <Layout
      title="Ministry Incubators"
      description="Helping business owners and entrepreneurs integrate Business as a Mission (BAM) — becoming marketplace ministers who create value, serve communities, and advance the gospel through their work."
      path="/ministry-incubators"
      image="/img/og-default.jpg"
      imageAlt="Ministry Incubators by Building on Truth"
      keywords="ministry incubator, business as mission, BAM, marketplace ministry, Christian entrepreneurship, kingdom business"
      jsonLd={incubatorsSchemas()}
    >
      {/* Hero */}
      <HeroSection>
        <HeroTitle>Ministry Incubators</HeroTitle>
        <HeroWave>
          <svg viewBox="0 0 1440 64" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,64 C360,0 1080,0 1440,64 L1440,64 L0,64 Z" fill="#fff" />
          </svg>
        </HeroWave>
      </HeroSection>

      <LeadSection>
        <LeadSubtitle>Your Business Is a Ministry. Let's Build It That Way.</LeadSubtitle>
        <LeadText>
          Our Ministry Incubator helps business owners and entrepreneurs become BAM practitioners — integrating gospel purpose into every part of how they build, lead, and grow.
        </LeadText>
        <LeadButtons>
          <HeroButton to="/contact">Start the Conversation</HeroButton>
        </LeadButtons>
      </LeadSection>

      {/* What is BAM */}
      <IntroSection>
        <SectionLabel>What is BAM?</SectionLabel>
        <SectionTitle>Business as a Mission is more than a strategy — it's a calling</SectionTitle>
        <BodyText>
          For too long, Christians in business have been told — implicitly or explicitly — that their "real" contribution to God's kingdom happens on Sunday, or through giving to missionaries, or by eventually leaving business to go into "full-time ministry." Business as a Mission (BAM) rejects that division entirely.
        </BodyText>
        <BodyText>
          BAM holds that your company — the jobs you create, the customers you serve, the culture you build, the problems you solve — can be a primary platform for gospel witness and kingdom impact. Not secondary. Not incidental. Primary.
        </BodyText>
        <BodyText>
          Our Ministry Incubator exists to help you understand that calling, embrace it fully, and build the kind of business that actually lives it out — financially sustainable, missionally intentional, and rooted in biblical faithfulness.
        </BodyText>
      </IntroSection>

      {/* Journey steps */}
      <JourneySection>
        <JourneyInner>
          <JourneyHeader>
            <SectionLabel>How We Help</SectionLabel>
            <SectionTitle>The BAM Journey</SectionTitle>
          </JourneyHeader>
          <StepsGrid>
            {STEPS.map((step) => (
              <StepCard key={step.number}>
                <StepNumber>{step.number}</StepNumber>
                <StepTitle>{step.title}</StepTitle>
                <StepText>{step.text}</StepText>
              </StepCard>
            ))}
          </StepsGrid>
        </JourneyInner>
      </JourneySection>

      {/* Who it's for */}
      <AudienceSection>
        <AudienceHeader>
          <SectionLabel>Who It's For</SectionLabel>
          <SectionTitle>BAM is for anyone in the marketplace</SectionTitle>
        </AudienceHeader>
        <AudienceGrid>
          {AUDIENCE.map((a) => (
            <AudienceCard key={a.title}>
              <AudienceIcon>{a.icon}</AudienceIcon>
              <AudienceTitle>{a.title}</AudienceTitle>
              <AudienceText>{a.text}</AudienceText>
            </AudienceCard>
          ))}
        </AudienceGrid>
      </AudienceSection>

      {/* Pull quote */}
      <QuoteSection>
        <QuoteMark>"</QuoteMark>
        <QuoteText>
          You are the light of the world. A city set on a hill cannot be hidden.
        </QuoteText>
        <QuoteSource>Matthew 5:14</QuoteSource>
      </QuoteSection>

      {/* CTA */}
      <CtaSection>
        <CtaTitle>Ready to build with mission at the center?</CtaTitle>
        <CtaText>
          Book a free call to talk through where you are and how our Ministry Incubator can help you move forward with clarity and conviction.
        </CtaText>
        <CtaButtons>
          <CtaButton to="/contact">Book a Free Call</CtaButton>
          <CtaSecondary to="/workshops">Explore Our Workshops</CtaSecondary>
        </CtaButtons>
      </CtaSection>
    </Layout>
  );
};

export default MinistryIncubators;
